import { useMemo, useState } from 'react'
import { createFileRoute, redirect, Link, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@connectrpc/connect-query'
import { create } from '@bufbuild/protobuf'
import { ArrowLeft, ArrowUpRight, FileText } from 'lucide-react'
import { z } from 'zod'

import { useBreadcrumbFromRoute } from '@/hooks/use-breadcrumb-from-route'
import { requireOrganizationFeature } from '@/lib/route-preloading'
import { useInquiry } from '@/stores/leads.store'
import { useInvalidateJobAfterQuoteChange } from '@/stores/pipeline.store'
import { useQuote } from '@/stores/quotes.store'
import { getJob } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import { GetJobRequestSchema } from '@/gen/cortado/v1/jobs_pb'
import { OrganizationFeatureEnum } from '@/gen/cortado/v1/settings_pb'
import type { Quote } from '@/gen/cortado/v1/invoices_pb'
import { Card, CardContent } from '@/components/ui/card'
import { SkeletonBar } from '@/components/shared/skeleton-bar'
import { Empty, EmptyDescription, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { QuoteStatusBadge } from '@/components/shared/enum-badge'
import { QuoteRejectionNote } from '@/components/quotes/quote-rejection-note'
import { QuoteEditor } from '@/components/quote-editor/quote-editor'
import { NewQuoteEditor } from '@/components/quote-editor/new-quote-editor'
import type { NewQuoteStart } from '@/components/quote-editor/use-new-quote-draft'
import { QuoteEditorActions } from '@/components/quote-editor/quote-editor-actions'
import { QuoteEditorRail } from '@/components/quote-editor/quote-editor-rail'
import type { QuoteEditorSummaryState } from '@/components/quote-editor/quote-editor-summary'
import { JobRail } from '@/components/pipeline/job-rail'
import { JobContactCard } from '@/components/pipeline/job-contact-card'
import { JobOriginalInquiry } from '@/components/pipeline/job-original-inquiry'
import { jobChildBreadcrumbs } from '@/components/pipeline/pipeline-breadcrumbs'
import { PipelineStageCell } from '@/components/pipeline/pipeline-stage-cell'
import { quoteStageCells } from '@/components/pipeline/quote-stage-view'
import { QuoteSwitcher } from '@/components/pipeline/quote-switcher'
import { NEW_QUOTE_FROM_REQUEST, NEW_QUOTE_ID, quoteBelongsToJob } from '@/components/pipeline/pipeline-quote-route'

const UNTITLED_JOB_TITLE = 'Untitled job'

/** A new quote being drafted on this page; `key` remounts the editor for each fresh draft. */
interface DraftSession {
  key: number
  createdQuoteId: string | null
  /** Where the draft started, fixed for its life (the URL drops it once the quote exists). */
  start: NewQuoteStart
}

// `$id_` opts out of nesting under the job page route: this is its own page,
// not an outlet inside the job detail.
export const Route = createFileRoute('/_app/_home/pipeline/$id_/quotes/$quoteId')({
  // For a new quote: a template id, or NEW_QUOTE_FROM_REQUEST for none.
  validateSearch: z.object({ start: z.string().optional() }),
  component: PipelineQuoteRouteFromParams,
  // Without the Pipeline, an existing quote still has its standalone page.
  beforeLoad: ({ context, params }) =>
    requireOrganizationFeature(context, OrganizationFeatureEnum.ORGANIZATION_FEATURE_JOBS, () =>
      params.quoteId === NEW_QUOTE_ID
        ? redirect({ to: '/dashboard', replace: true })
        : redirect({ to: '/quotes/$id', params: { id: params.quoteId }, replace: true }),
    ),
})

function PipelineQuoteRouteFromParams() {
  const { id, quoteId } = Route.useParams()
  const { start } = Route.useSearch()
  return <PipelineQuotePage jobId={id} quoteId={quoteId} start={start} />
}

interface PipelineQuoteRouteProps {
  jobId: string
  quoteId: string
  /** `?start=` for a new quote. */
  start?: string
}

function newQuoteStart(start: string | undefined): NewQuoteStart {
  if (!start) return undefined
  return start === NEW_QUOTE_FROM_REQUEST ? 'request' : { templateId: start }
}

function NotFound({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <div className="flex flex-1 flex-col rounded-lg border border-dashed py-6 md:py-0">
        <Empty>
          <EmptyMedia variant="icon">
            <FileText className="h-6 w-6" />
          </EmptyMedia>
          <EmptyTitle>{title}</EmptyTitle>
          <EmptyDescription>{description}</EmptyDescription>
        </Empty>
      </div>
    </div>
  )
}

/**
 * One quote of a pipeline job: the quote editor with the job's contact,
 * original request, and activity alongside for reference. `quoteId` "new"
 * drafts a quote from the job's inquiry; once the draft is first saved the URL
 * moves to the created quote while the same editor keeps editing it.
 */
export function PipelineQuotePage({ jobId, quoteId, start }: PipelineQuoteRouteProps) {
  const navigate = useNavigate()
  const invalidateJob = useInvalidateJobAfterQuoteChange()

  // A route param change keeps this component mounted, so a draft opened here
  // stays in its own editor after the URL moves to the quote it created. Each
  // visit to "new" (including from the quote switcher) starts a fresh draft;
  // switching to any other quote ends it.
  const [draftSession, setDraftSession] = useState<DraftSession | null>(
    quoteId === NEW_QUOTE_ID ? { key: 0, createdQuoteId: null, start: newQuoteStart(start) } : null,
  )
  // The editor reports what it shows after every change, for the summary rail.
  const [summary, setSummary] = useState<QuoteEditorSummaryState | undefined>(undefined)
  const [previousQuoteId, setPreviousQuoteId] = useState(quoteId)
  const [previousStart, setPreviousStart] = useState(start)
  // Picking another starting point while drafting starts the draft over.
  if (quoteId === NEW_QUOTE_ID && quoteId === previousQuoteId && start !== previousStart) {
    setPreviousStart(start)
    setSummary(undefined)
    setDraftSession({ key: (draftSession?.key ?? 0) + 1, createdQuoteId: null, start: newQuoteStart(start) })
  }
  if (quoteId !== previousQuoteId) {
    setPreviousQuoteId(quoteId)
    setPreviousStart(start)
    // A draft keeps its editor when the URL moves to the quote it created; any other move gets a new one.
    if (!(draftSession && quoteId === draftSession.createdQuoteId)) setSummary(undefined)
    if (quoteId === NEW_QUOTE_ID) {
      setDraftSession({ key: (draftSession?.key ?? 0) + 1, createdQuoteId: null, start: newQuoteStart(start) })
    } else if (draftSession && quoteId !== draftSession.createdQuoteId) {
      setDraftSession(null)
    }
  }
  const isDraftSession = draftSession !== null
  const savedQuoteId = quoteId === NEW_QUOTE_ID ? (draftSession?.createdQuoteId ?? null) : quoteId

  const jobRequest = useMemo(() => create(GetJobRequestSchema, { jobId }), [jobId])
  const jobQuery = useQuery(getJob, jobRequest, { enabled: Boolean(jobId) })
  const job = jobQuery.data?.job
  const { inquiry, isLoading: isInquiryLoading } = useInquiry(job?.inquiryId)
  const { quote, isLoading: isQuoteLoading, isError: isQuoteError } = useQuote(savedQuoteId ?? undefined)
  const heading = quote ? `Quote #${quote.quoteNumber}` : 'New quote'
  const breadcrumbs = useMemo(() => (job ? jobChildBreadcrumbs(job, heading) : undefined), [job, heading])
  useBreadcrumbFromRoute(breadcrumbs)

  if (jobQuery.isLoading || (!isDraftSession && isQuoteLoading)) {
    return (
      <div className="flex h-full flex-col gap-4 p-4">
        <div className="space-y-2">
          <SkeletonBar className="h-4 w-48" />
          <SkeletonBar className="h-8 w-64" />
        </div>
        <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
          <SkeletonBar className="h-96 w-full rounded-xl" />
          <SkeletonBar className="h-64 w-full rounded-xl" />
        </div>
      </div>
    )
  }

  if (jobQuery.isError || !job) {
    return <NotFound title="Job not found" description="We couldn't load this job. It may have been removed." />
  }

  if (!isDraftSession && (isQuoteError || !quote || !quoteBelongsToJob(quote, job))) {
    return <NotFound title="Quote not found" description="This quote doesn't exist or belongs to a different job." />
  }

  if (isDraftSession && !job.inquiryId) {
    return <NotFound title="Can't draft a quote" description="This job has no linked inquiry to quote from." />
  }

  function openQuote(id: string, replace = false) {
    navigate({ to: '/pipeline/$id/quotes/$quoteId', params: { id: jobId, quoteId: id }, replace })
  }

  const callbacks = {
    onQuoteDuplicated: (copy: Quote) => {
      void invalidateJob()
      openQuote(copy.id)
    },
    onQuoteDeleted: () => {
      void invalidateJob()
      navigate({ to: '/pipeline/$id', params: { id: jobId } })
    },
    onQuoteChanged: () => void invalidateJob(),
    onSummaryChange: setSummary,
    // Send, status and the rest of the quote's actions live in the page header.
    hideActions: true,
  }

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto p-4">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">{heading}</h1>
          {quote && <QuoteStatusBadge status={quote.status} />}
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs/normal text-muted-foreground">
          <Link to="/pipeline/$id" params={{ id: job.id }} className="inline-flex items-center gap-1 hover:text-foreground">
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to {job.title || UNTITLED_JOB_TITLE}
          </Link>
          {quote && (
            <Link
              to="/quotes/$id"
              params={{ id: quote.id }}
              className="inline-flex items-center gap-1 hover:text-foreground"
            >
              Activity &amp; engagement
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          )}
        </div>
        {summary?.quote && (
          <div className="mt-3">
            <QuoteEditorActions
              variant="header"
              quote={summary.quote}
              inquiry={inquiry}
              organizationId={inquiry?.organizationId || summary.quote.organizationId}
              values={summary.values}
              totals={summary.totals}
              allLineItems={summary.allLineItems}
              prepareToSend={summary.prepareToSend}
              onQuoteDuplicated={callbacks.onQuoteDuplicated}
              onQuoteDeleted={callbacks.onQuoteDeleted}
              onQuoteChanged={callbacks.onQuoteChanged}
            />
          </div>
        )}
        {quote && (
          <QuoteRejectionNote
            status={quote.status}
            rejectedAt={quote.rejectedAt}
            category={quote.rejectionReasonCategory}
            reason={quote.rejectionReason}
          />
        )}
      </div>

      <QuoteSwitcher
        jobId={job.id}
        organizationId={inquiry?.organizationId || quote?.organizationId || ''}
        eventTypeKey={inquiry?.eventTypeKey || undefined}
        quoteIds={job.quoteIds}
        acceptedQuoteId={job.acceptedQuoteId}
        currentQuote={quote}
        canDraft={Boolean(job.inquiryId)}
        onDuplicated={callbacks.onQuoteDuplicated}
      />

      {/* Draft / sent / viewed / decision, in the job page's stepper style. */}
      <div className="grid grid-cols-1 divide-y rounded-xl border bg-card sm:grid-cols-2 lg:grid-cols-4 lg:divide-x lg:divide-y-0">
        {quoteStageCells(quote).map((cell, index) => (
          <PipelineStageCell key={cell.label} index={index} {...cell} />
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
        <Card className="min-w-0 py-4">
          <CardContent>
            {draftSession ? (
              <NewQuoteEditor
                key={draftSession.key}
                inquiry={inquiry}
                fallbackTitle={job.title || 'New quote'}
                start={draftSession.start}
                onCreated={(created) => {
                  setDraftSession((current) => current && { ...current, createdQuoteId: created.id })
                  void invalidateJob()
                  openQuote(created.id, true)
                }}
                {...callbacks}
              />
            ) : (
              quote && (
                <QuoteEditor
                  // A duplicate opens on this same route; a fresh form per quote.
                  key={quote.id}
                  quote={quote}
                  inquiry={inquiry}
                  organizationId={inquiry?.organizationId || quote.organizationId}
                  {...callbacks}
                />
              )
            )}
          </CardContent>
        </Card>

        <div className="flex min-w-0 flex-col gap-4">
          <QuoteEditorRail
            summary={summary}
            organizationId={inquiry?.organizationId || quote?.organizationId || ''}
            request={
              <>
                <JobContactCard job={job} />
                {job.inquiryId && <JobOriginalInquiry inquiry={inquiry} isLoading={isInquiryLoading} jobId={job.id} />}
              </>
            }
          />
          <JobRail job={job} stage="quote" />
        </div>
      </div>
    </div>
  )
}
